/* import __COLOCATED_TEMPLATE__ from './admin-blaster-results.hbs'; */
import { Promise } from 'rsvp';
import { computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { showModal } from 'sh-www/utils/modal-controls';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { ADMIN_BLASTER__SORT_NAMES } from "../../../constants/routes/admin/blaster";
import { stopClickEvent } from "../../../utils/events";
import { ASSIGNMENT } from "../../../models/assignment";

/**
 * This will show all the results from an admin blaster search
 */
export default Component.extend({
  /**
   * @inheritDoc
   */
  tagName: 'div',
  classNames: ['SH-c-admin-blaster-results'],

  /**
   * Register services
   */
  errorTracker: service('error-tracker'),
  state: service('states/routes/admin/blaster'),
  stateManager: service('state-managers/routes/admin/blaster'),

  /*********************************
   * REQUIRED PARAMS
   *********************************/

  /*********************************
   * OPTIONAL PARAMS
   **********************************/

  /*********************************
   * LIFECYCLE FUNCTIONS
   **********************************/

  /*********************************
   * PRIVATE
   **********************************/

  /**
   * @type {Boolean}    True when the form elements should be disabled
   */
  _areFormElementsDisabled: readOnly('state.areFormElementsDisabled'),

  /**
   * Results that have been filtered out
   *
   * @type {Ember.A<FreelancerSearchResult>}
   */
  _blastFilteredResults: readOnly('state.blastFilteredResultsSorted'),

  /**
   * Valid blast results
   *
   * @type {Ember.A<FreelancerSearchResult>}
   */
  _blastResults: readOnly('state.blastResultsSorted'),

  /**
   * @type {Object}     Constant enums
   */
  _constantsAdminSortNames: ADMIN_BLASTER__SORT_NAMES,

  /**
   * @type {Boolean}    True when the blast button should be disabled.
   */
  _isBlastButtonDisabled: computed('state.isBlastButtonEnabled', function() {
    return !this.get('state.isBlastButtonEnabled');
  }),

  /**
   * @type {Boolean}    True when a search yields no results.
   */
  _showNoResultsCallout: computed(
    'state.freelancerBlastSearchResultCollection.id',
    '_blastResults.[]',
    '_blastFilteredResults.[]',
    function() {
      return Boolean(!this._blastResults.length && !this._blastFilteredResults.length && this.state.freelancerBlastSearchResultCollection);
  }),

  /**
   * @type {Boolean}    True if select all good results is checked.
   */
  _isSelectAllResultsChecked: readOnly('state.isSelectAllResultsChecked'),

  /**
   * @type {Assignment}   The current posting in the blaster view.
   */
  _posting: readOnly('state.currentAssignment'),

  actions: {
    /**
     * Admin has clicked to blast the hunt.
     */
    onBlastTheHuntClicked: function() {
      const blastFunction = () => {
        this.get('stateManager').blastTheHunt().then(() => {
          let modalModel = {
            isShowCancelButton: false,
            bodyText: 'BLASTED!',
            titleText: `Blast Succeeded`,
          };

          showModal(this, 'confirmation', modalModel);
        }).catch(e => {
          handleErrorResponseWithModal(this, e);
        });
      };

      if (this.get('_posting.privacyLevel') === ASSIGNMENT.PRIVACY_LEVEL.PUBLIC) {
        return blastFunction();
      } else if (this.get('_posting.select') === true) {
        this.confirmBlastSelectAssignment()
          .then(() => blastFunction())
          .catch(() => {});
      }

      this.confirmBlastPrivateAssignment()
        .then(() => blastFunction())
        .catch(() => {});
    },

    /**
     * When the admin toggles a specific sort.
     *
     * @param {String} sortName     ADMIN_BLASTER__SORT_NAMES.*
     */
    onFilteredResultsSortChanged: function(sortName) {
      this.get('stateManager').filteredResultsSortChanged(sortName);
    },

    /**
     * When the admin toggles a specific sort.
     *
     * @param {String} sortName     ADMIN_BLASTER__SORT_NAMES.*
     */
    onResultsSortChanged: function(sortName) {
      this.get('stateManager').resultsSortChanged(sortName);
    },

    /**
     * Called when the user clicks to select all results to send to blaster.
     */
    onSelectAllResultsClicked: function() {
      this.get('stateManager').updateSelectAllResultsChecked(!this.get('state.isSelectAllResultsChecked'));
    }
  },

  /**
   * Show the user a confirmation message to blast a private assignment
   *
   * @return {Ember.RSVP.Promise}   resolves to true when the user has confirmed, false otherwise
   */
  confirmBlastPrivateAssignment: function() {
    return new Promise((resolve, reject) => {
      showModal(this, 'confirmation', {
        isShowCancelButton: true,
        titleText: 'Private Posting',
        bodyText: 'This is a private posting. Are you certain you want to blast it?',
        confirmAction: () => {
          resolve();
        },
        cancelAction: () => {
          reject();
        }
      });
    });
  },

  /**
   * Show the user a confirmation message to blast a private assignment
   *
   * @return {Ember.RSVP.Promise}   resolves to true when the user has confirmed, false otherwise
   */
  confirmBlastSelectAssignment: function() {
    return new Promise((resolve, reject) => {
      showModal(this, 'confirmation', {
        isShowCancelButton: true,
        titleText: 'Select Posting',
        bodyText: 'This is a Select posting. Are you sure you want to blast it?',
        confirmAction: () => {
          resolve();
        },
        cancelAction: () => {
          reject();
        }
      });
    });
  }
});
